import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from 'services/api';
import routes from 'routes';
import { FLAGS } from 'constants/classifier';

import Layout from 'components/Layout/Layout';
import Button from 'components/common/Button';
import Label from 'components/common/Label';
import Table from 'components/common/Table';

const ACTION_COLORS = { block: 'danger', challenge: 'warning', allow: 'success' };

const useNetworks = (currentLogger) => {
    const [data, setData] = useState({
        blocks: [],
        asns: [],
        updatedAt: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [tick, setTick] = useState(0);

    const reload = useCallback(() => {
        setTick((prev) => prev + 1);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = () => {
            api.getNetworks(currentLogger, (res) => {
                if (cancelled) return;
                if (res.data.status === 'error' || !Array.isArray(res.data.blocks)) {
                    console.error(res.data.errorMsg);
                    setError(true);
                } else {
                    setData({ ...res.data, updatedAt: Date.now() });
                    setError(false);
                }
                setLoading(false);
            });
        };
        setLoading(true);
        load();
        return () => {
            cancelled = true;
        };
    }, [currentLogger, tick]);

    return { data, loading, error, reload };
};

const useNow = (intervalMs) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
    return now;
};

function ago(timestampMs) {
    const s = Math.max(0, Math.round((Date.now() - timestampMs) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
}

function duration(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) {
        return `${s}s`;
    }
    if (s < 3600) {
        return `${Math.floor(s / 60)}m`;
    }
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

const percent = (part, total) => (Number(total) > 0 ? Math.round((Number(part) / Number(total)) * 100) : 0);

const flagNames = (flags) => Object.keys(FLAGS).filter((name) => (flags & FLAGS[name]) !== 0);

const badRatio = ({ bad, total }) => `${bad}/${total} bad (${percent(bad, total)}%)`;

// promote() only challenges a block when it holds trusted requesters or sits on an eyeball network
const challengeReason = (block) =>
    Number(block.evidence.trusted) > 0 ? 'has trusted requesters' : 'residential/mobile network';

const BlockEvidence = ({ evidence, needs }) => (
    <span title={needs ? `Needs at least ${needs.minBad} bad and ${needs.ratio * 100}% bad` : undefined}>
        {badRatio(evidence)}
        {Number(evidence.trusted) > 0 && ` · ${evidence.trusted} trusted`}
    </span>
);

const AsnEvidence = ({ evidence, needs }) => (
    <span
        title={needs
            ? `Needs at least ${needs.minBad} bad across ${needs.minBadBlocks} blocks and ${needs.ratio * 100}% bad`
            : undefined}
    >
        {badRatio(evidence)}
        {` · ${evidence.badBlocks} blocks`}
    </span>
);

const Members = ({ loggerId, kind, id }) => {
    const [state, setState] = useState({ loading: true, error: false, members: [] });

    useEffect(() => {
        let cancelled = false;
        const done = (res) => {
            if (cancelled) {
                return;
            }
            if (res.data.status === 'error' || !Array.isArray(res.data.members)) {
                setState({ loading: false, error: true, members: [] });
            } else {
                setState({ loading: false, error: false, members: res.data.members });
            }
        };
        setState({ loading: true, error: false, members: [] });
        if (kind === 'block') {
            api.getBlockMembers(loggerId, id, done);
        } else {
            api.getAsnMembers(loggerId, id, done);
        }
        return () => {
            cancelled = true;
        };
    }, [loggerId, kind, id]);

    if (state.loading) {
        return <p className="is-size-7">Loading requesters...</p>;
    }
    if (state.error) {
        return <p className="is-size-7 has-text-danger">Could not load requesters.</p>;
    }
    if (state.members.length === 0) {
        return <p className="is-size-7">No requesters seen in the last 24 hours.</p>;
    }

    return (
        <>
            <p className="is-size-7 mb-2">
                Requesters seen in the last 24 hours, highest score first
                {state.members.length >= 200 && ' (first 200)'}
                .
            </p>
            <table className="table is-narrow is-fullwidth is-size-7">
                <thead>
                    <tr>
                        <th>IP Address</th>
                        <th>Score</th>
                        <th>Action</th>
                        <th>Requests</th>
                        <th>Country</th>
                        <th>Last seen</th>
                        <th>Flags</th>
                    </tr>
                </thead>
                <tbody>
                    {state.members.map((m) => (
                        <tr key={m.id}>
                            <td>
                                <Link to={routes.requestersRequests.ref(loggerId, m.id)}>{m.ipAddress}</Link>
                            </td>
                            <td>{m.score !== null && m.score !== undefined ? Math.round(m.score) : 'N/A'}</td>
                            <td>
                                {m.isBlacklist ? 'blacklist' : m.isWhitelist ? 'whitelist' : m.action ?? 'N/A'}
                            </td>
                            <td>{m.totalRequests}</td>
                            <td>{m.country ? m.country.toUpperCase() : 'N/A'}</td>
                            <td>{m.lastAccess ? ago(new Date(m.lastAccess).getTime()) : 'N/A'}</td>
                            <td>{flagNames(m.flags).join(', ') || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

const Networks = () => {
    const currentLogger = useSelector((state) => state.global.currentLogger);

    const { data, loading, error, reload } = useNetworks(currentLogger);

    const [action, setAction] = useState('all');
    const [query, setQuery] = useState('');

    const blocks = data.blocks
        .filter((b) => (action === 'all' || b.action === action) && b.prefix.includes(query.trim()))
        .sort((a, b) => (b.action === 'block') - (a.action === 'block') || b.evidence.bad - a.evidence.bad);

    const asnQuery = query.trim().toLowerCase().replace(/^as/, '');
    const asns = data.asns
        .filter((a) => action === 'all' && a.asn.includes(asnQuery))
        .sort((a, b) => b.evidence.bad - a.evidence.bad);

    const now = useNow(5000);
    const blockRows = blocks.map((b) => [
        { id: b.prefix, kind: 'block' },
        b.prefix,
        <React.Fragment key="action">
            <Label color={ACTION_COLORS[b.action] ?? 'grey'}>{b.action}</Label>
            {b.action === 'challenge' && <p className="is-size-7 mt-1">{challengeReason(b)}</p>}
        </React.Fragment>,
        <BlockEvidence key="evidence" evidence={b.evidence} needs={b.needs} />,
        ago(b.evaluatedAt),
        duration(b.expiresAt - now),
    ]);
    const asnRows = asns.map((a) => [
        { id: a.asn, kind: 'asn' },
        <span key="asn" title={a.classification ?? undefined}>
            AS
            {a.asn}
            {a.name && a.name !== 'Unknown' && <span className="is-block is-size-7">{a.name}</span>}
        </span>,
        <span key="status" title="Requests from this ASN get the ASN_FLAGGED flag added to their score">
            <Label color="warning">flagged</Label>
        </span>,
        <AsnEvidence key="evidence" evidence={a.evidence} needs={a.needs} />,
        ago(a.evaluatedAt),
        duration(a.expiresAt - now),
    ]);

    const renderMembers = (row) => <Members loggerId={currentLogger} kind={row.kind} id={row.id} />;

    const empty = data.blocks.length === 0 && data.asns.length === 0;

    return (
        <Layout title="Networks">
            <div className="is-flex is-justify-content-space-between is-align-items-center mb-1">
                <h1 className="title mb-0">Networks</h1>
                <span className="is-flex is-align-items-center is-size-7">
                    {loading ? 'Loading...' : data.updatedAt ? `Updated ${ago(data.updatedAt)}` : 'Not loaded yet'}
                    <Button
                        className="ml-2"
                        size="small"
                        onClick={reload}
                    >
                        Refresh
                    </Button>
                </span>
            </div>

            <p className="has-text-weight-light">
                Ranges and networks the classifier is treating as bad. A verdict expires unless the evidence
                keeps holding. Click a row to see its requesters.
            </p>
            {error && (
                <p className="has-text-danger is-size-7 mt-2">Could not load networks. Showing the last loaded data.</p>
            )}
            {loading && !data.updatedAt ? null : empty ? (
                <p className="meta mt-4">No networks or ASNs are currently being treated as bad.</p>
            ) : (
                <>
                    <div className="is-flex mt-4">
                        {['all', 'block', 'challenge'].map((a) => (
                            <Button
                                key={a}
                                rounded
                                size="small"
                                onClick={() => setAction(a)}
                                type={action === a ? 'filled' : 'outlined'}
                                className="activity-filter-btn"
                            >
                                {a === 'all' ? 'All' : a === 'block' ? 'Block' : 'Challenge'}
                            </Button>
                        ))}
                        <input
                            className="input is-rounded search-input is-small ml-2"
                            style={{ maxWidth: '200px' }}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter by range or AS number"
                            aria-label="Filter by range or AS number"
                        />
                    </div>
                    <section>
                        <h2 className="subtitle mt-5">IP Ranges</h2>
                        {blockRows.length === 0 ? (
                            <p className="meta">No ranges match.</p>
                        ) : (
                            <Table
                                headerAttributes={['Range', 'Action', 'Evidence', 'Last confirmed', 'Expires In']}
                                data={blockRows}
                                renderExpandedRow={renderMembers}
                            />
                        )}
                    </section>
                    <section>
                        <h2 className="subtitle mt-5">ASNs</h2>
                        {asnRows.length === 0 ? (
                            <p className="meta">
                                {action === 'all' ? 'No ASNs match.' : 'ASNs are flagged rather than blocked or challenged.'}
                            </p>
                        ) : (
                            <Table
                                headerAttributes={['ASN', 'Status', 'Evidence', 'Last confirmed', 'Expires In']}
                                data={asnRows}
                                renderExpandedRow={renderMembers}
                            />
                        )}
                    </section>
                </>
            )}
        </Layout>
    );
};

const evidenceShape = PropTypes.shape({
    bad: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trusted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    badBlocks: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

BlockEvidence.propTypes = { evidence: evidenceShape.isRequired, needs: PropTypes.object };
AsnEvidence.propTypes = { evidence: evidenceShape.isRequired, needs: PropTypes.object };
Members.propTypes = {
    loggerId: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(['block', 'asn']).isRequired,
    id: PropTypes.string.isRequired,
};

export default Networks;
